import { defineStore } from "pinia";
import {
  handleError,
  getCookie,
  setCookie,
  removeCookie,
  migrateWrappingToV2,
  verifyPassphrase,
} from "@/utils/common.js";
import { authData } from "@/api/apiService";
import {
  ENCRYPTION,
  ENCRYPTION_ROTATE,
  ENCRYPTION_DATA,
  ENCRYPTION_STATISTICS,
  CUSTOMER_OBJECT,
  AGENTS,
  AGENT_ENCRYPTION,
  AGENT_RELEASE_KEY,
} from "@/api/apiRoute";
import { errorMessage } from "@/utils/errorMessages";
import {
  unwrapV2,
  rewrapToV2,
  randomVaultKey,
  isV2Envelope,
  isV2DataEnvelope,
  dataEnvelopeGeneration,
  wrapKeyForAgent,
  unwrapKeyAsAgent,
} from "@/utils/encryptionV2";
import {
  setKeyring,
  clearKeyring,
  getCurrentGeneration,
} from "@/utils/keyring";
import { EncryptionService } from "@/utils/encryptionService";

export const useSecurityStore = defineStore("securityStore", {
  state: () => ({
    decryptedEncryptionKey: getCookie("decryptedEncryptionKey", null, "string"),
    passphraseVerifiedAt: getCookie("passphraseVerifiedAt", null, "string"),
    keyExpirationTimer: null,
    errorDisplayDuration: 4000,
    errorMsg: "",
    loading: false,
    encryptionInfo: null,
    encryptionDetails: null,
    hint: null,
    passphrase: null,
    verifiedEncrypted: false,
    IsPassphraseValid: false,
    errorMessage: "",
    encryptionStatistics: null,
    keyTimestamp: null,
    // Auto-expiration settings
    keyValidityDuration: 10 * 60 * 1000, // 10 minutes in milliseconds
    autoExpirationEnabled: true,
    lastActivityTimestamp: null,
    isConfirmationDialogOpen: false,
    shouldShowConfirmationDialog: false,
  }),

  getters: {
    // Enhanced key expiration getter with better logging
    isKeyExpired() {
      const now = new Date();

      // If no verification timestamp, consider expired
      if (!this.passphraseVerifiedAt) {
        return true;
      }

      try {
        const verifiedAt = new Date(this.passphraseVerifiedAt);

        // Check if verifiedAt is valid
        if (isNaN(verifiedAt.getTime())) {
          console.error(
            " Invalid passphraseVerifiedAt timestamp:",
            this.passphraseVerifiedAt
          );
          return true;
        }

        const diffMs = now - verifiedAt;
        const diffMinutes = diffMs / (1000 * 60);
        const isExpired = diffMinutes > 10; // 10 minutes
        return isExpired;
      } catch (error) {
        console.error("Error checking key expiration:", error);
        return true;
      }
    },

    // Check if key is valid and not expired
    hasValidKey() {
      return (
        this.decryptedEncryptionKey &&
        this.IsPassphraseValid &&
        !this.isKeyExpired
      );
    },

    // Get remaining key validity time in milliseconds
    remainingKeyTime() {
      if (!this.passphraseVerifiedAt) return 0;

      try {
        const verifiedAt = new Date(this.passphraseVerifiedAt);
        const now = new Date();
        const elapsed = now - verifiedAt;
        const remaining = this.keyValidityDuration - elapsed;

        return Math.max(0, remaining);
      } catch (error) {
        console.error("Error calculating remaining key time:", error);
        return 0;
      }
    },

    // Get remaining time in human readable format
    remainingKeyTimeFormatted() {
      const remaining = this.remainingKeyTime;
      if (remaining <= 0) return "Expired";

      const minutes = Math.floor(remaining / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

      return `${minutes}m ${seconds}s`;
    },
  },

  actions: {
    // Enhanced setEncryptionData with better timestamp handling
    setEncryptionData(data, customTimestamp = null) {
      this.decryptedEncryptionKey = data.result.encryptionKey;
      this.IsPassphraseValid = data.result.valid;

      // Use custom timestamp or current time
      const timestamp = customTimestamp || new Date().toISOString();
      this.passphraseVerifiedAt = timestamp;
      this.keyTimestamp = Date.now();
      this.lastActivityTimestamp = Date.now();

      // Store in cookies for persistence (optional, based on your security requirements)
      setCookie("decryptedEncryptionKey", this.decryptedEncryptionKey, {
        expires: 1 / 24,
      }); // 1 hour expiry
      setCookie("passphraseVerifiedAt", this.passphraseVerifiedAt, {
        expires: 1 / 24,
      });
      // Set up auto-expiration timer
      if (this.autoExpirationEnabled) {
        this.setupAutoExpiration();
      }
    },

    // Enhanced clearEncryptionData with better cleanup
    clearEncryptionData() {
      // Clear state
      this.decryptedEncryptionKey = null;
      this.keyTimestamp = null;
      this.IsPassphraseValid = false;
      this.passphraseVerifiedAt = null;
      this.lastActivityTimestamp = null;

      // Clear cookies
      removeCookie("decryptedEncryptionKey");
      removeCookie("passphraseVerifiedAt");

      // Drop in-memory vault keys (current + rotation history generations)
      clearKeyring();

      // Clear auto-expiration timer
      this.clearAutoExpiration();
    },

    // Set up automatic key expiration
    setupAutoExpiration() {
      // Clear any existing timer
      this.clearAutoExpiration();

      const remaining = this.remainingKeyTime;
      if (remaining <= 0) {
        this.clearEncryptionData();
        return;
      }
      this.keyExpirationTimer = setTimeout(() => {
        this.handleAutoExpiration();
      }, remaining);
    },

    // Clear auto-expiration timer
    clearAutoExpiration() {
      if (this.keyExpirationTimer) {
        clearTimeout(this.keyExpirationTimer);
        this.keyExpirationTimer = null;
      }
    },

    // Handle automatic expiration
    handleAutoExpiration() {
      // Clear encryption data
      this.clearEncryptionData();

      // Emit custom event for components to handle
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("encryption-key-expired", {
            detail: {
              timestamp: new Date().toISOString(),
              reason: "auto-expiration",
            },
          })
        );
      }
    },

    // Update last activity timestamp to extend key validity
    updateLastActivity() {
      if (this.hasValidKey) {
        this.lastActivityTimestamp = Date.now();
      }
    },

    // Enhanced key expiration check with activity tracking
    checkKeyExpiry() {
      const expired = this.isKeyExpired;

      if (expired && this.hasValidKey) {
        this.clearEncryptionData();
        return true;
      }

      return expired;
    },

    // Extend key validity (reset timer)
    extendKeyValidity() {
      if (!this.hasValidKey) {
        console.warn(" Cannot extend validity - no valid key");
        return false;
      }

      this.passphraseVerifiedAt = new Date().toISOString();
      this.lastActivityTimestamp = Date.now();

      // Update cookie
      setCookie("passphraseVerifiedAt", this.passphraseVerifiedAt, {
        expires: 1 / 24,
      });

      // Reset auto-expiration timer
      if (this.autoExpirationEnabled) {
        this.setupAutoExpiration();
      }

      return true;
    },

    // Validate encryption key format
    validateEncryptionKey(key) {
      if (!key || typeof key !== "string") {
        return { valid: false, reason: "Invalid key format" };
      }

      // Check if it's a hex string (128 characters for 256-bit key)
      if (key.length === 128 && /^[a-fA-F0-9]+$/.test(key)) {
        return { valid: true, format: "hex" };
      }

      // Check if it's a base64 string
      if (key.length > 20 && /^[A-Za-z0-9+/]*={0,2}$/.test(key)) {
        return { valid: true, format: "base64" };
      }

      // Other string formats might be valid
      if (key.length > 10) {
        return { valid: true, format: "string" };
      }

      return { valid: false, reason: "Key too short or invalid format" };
    },

    // Get encryption key info for debugging
    getKeyInfo() {
      return {
        hasKey: !!this.decryptedEncryptionKey,
        keyLength: this.decryptedEncryptionKey?.length || 0,
        isValid: this.IsPassphraseValid,
        verifiedAt: this.passphraseVerifiedAt,
        isExpired: this.isKeyExpired,
        remainingTime: this.remainingKeyTimeFormatted,
        keyValidation: this.decryptedEncryptionKey
          ? this.validateEncryptionKey(this.decryptedEncryptionKey)
          : null,
      };
    },

    // Initialize store (call this when app starts)
    initializeStore() {
      // Check if we have stored credentials
      const storedKey = getCookie("decryptedEncryptionKey", null, "string");
      const storedTimestamp = getCookie("passphraseVerifiedAt", null, "string");

      if (storedKey && storedTimestamp) {
        // Validate stored data
        const keyValidation = this.validateEncryptionKey(storedKey);
        if (keyValidation.valid) {
          this.decryptedEncryptionKey = storedKey;
          this.passphraseVerifiedAt = storedTimestamp;
          this.IsPassphraseValid = true;

          // Check if still valid
          if (!this.isKeyExpired) {
            if (this.autoExpirationEnabled) {
              this.setupAutoExpiration();
            }
          } else {
            this.clearEncryptionData();
          }
        } else {
          console.warn("Stored key format invalid, clearing");
          this.clearEncryptionData();
        }
      }
    },

    // Error handling methods (keeping existing functionality)
    clearErrorMessage() {
      this.errorMsg = "";
    },

    setErrorMessage(error) {
      const message = errorMessage[error] || error || "An error occurred";
      this.errorMsg = message;
    },

    showError(error) {
      this.setErrorMessage(error?.response?.data?.error);
      handleError(error);
      throw error;
    },

    // API methods (keeping existing functionality)
    async getEncryptionStatus() {
      this.loading = true;
      try {
        const result = await authData.get(ENCRYPTION);
        this.encryptionDetails = result.data.data;
        this.clearErrorMessage();
        return result;
      } catch (error) {
        console.error("Failed to fetch encryption status:", error);
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Lazily migrate a legacy (v1) key wrapping to v2 after a successful
     * unlock. Safe to call on every unlock: it no-ops when the account is
     * already v2 or when migration can't complete. Non-fatal on error so the
     * user's session is never blocked by it.
     * See utils/encryptionV2.js and docs/encryption-v2-design.md (Tier A).
     * @returns {Promise<boolean>} true if a migration was persisted
     */
    async ensureV2Wrapping(passphrase) {
      try {
        const details = this.encryptionDetails;
        if (!passphrase || !details?.encryptedEncryptionKey) return false;
        const payload = await migrateWrappingToV2(passphrase, details);
        if (!payload) return false; // already v2, or passphrase mismatch
        await this.saveEncryption({ ...payload, hint: details.hint });
        return true;
      } catch (error) {
        console.error("v2 wrapping migration failed (non-fatal):", error);
        return false;
      }
    },

    async saveEncryption(data) {
      this.loading = true;
      try {
        const result = await authData.post(ENCRYPTION, data);
        this.encryptionDetails = result.data.data;
        return result;
      } catch (error) {
        console.error("Failed to save encryption data:", error);
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Parse the server-stored encryptionKeyHistory (JSON list of prior
     * generations' wrapped vault keys) into a safe array.
     * @returns {Array<{generation:number, encryptedEncryptionKey:string}>}
     */
    parseKeyHistory() {
      try {
        const h = JSON.parse(this.encryptionDetails?.encryptionKeyHistory || "[]");
        return Array.isArray(h) ? h : [];
      } catch {
        return [];
      }
    },

    /**
     * Build the in-memory keyring after an unlock so an EncryptionService can
     * decrypt data written under any live key generation (needed while a Tier B
     * rotation is in progress or incomplete). Requires the passphrase to unwrap
     * the historical wrapped keys. No-op for v1/pre-migration accounts (single
     * key, no history). Non-fatal: a failure just means old-generation data
     * stays unreadable until the next passphrase entry.
     * See docs/encryption-v2-design.md §4 and utils/keyring.js.
     */
    async buildKeyring(passphrase) {
      try {
        const details = this.encryptionDetails;
        if (
          !passphrase ||
          !this.decryptedEncryptionKey ||
          !isV2Envelope(details?.encryptedEncryptionKey)
        ) {
          clearKeyring();
          return;
        }
        const history = this.parseKeyHistory();
        const currentGeneration = history.length + 1;
        const entries = {};
        for (const h of history) {
          if (!h?.encryptedEncryptionKey || !h?.generation) continue;
          const u = await unwrapV2(passphrase, h.encryptedEncryptionKey);
          if (u.valid) entries[h.generation] = u.encryptionKey;
        }
        entries[currentGeneration] = this.decryptedEncryptionKey;
        setKeyring(entries, currentGeneration);
      } catch (error) {
        console.error("buildKeyring failed (non-fatal):", error);
        clearKeyring();
      }
    },

    /**
     * Tier B — rotate the vault key and re-encrypt all data under it.
     *
     * Mints a new vault key, re-wraps it (and the unchanged RSA keypair) under
     * the passphrase, appends the superseded key to encryptionKeyHistory, and
     * persists it via the dedicated rotate route. Then re-encrypts every
     * encrypted field from the old generation to the new one. The data envelope
     * carries its generation and the keyring retains old keys, so an
     * interrupted run leaves a readable, resumable (re-runnable) vault.
     *
     * @param {string} passphrase
     * @param {(p:{phase:string, done:number, total:number})=>void} onProgress
     * @returns {Promise<{newGeneration:number, fieldsRotated:number, filesPending:number, failures:number}>}
     */
    async rotateVaultKey(passphrase, onProgress = () => {}) {
      const details = this.encryptionDetails;
      if (!passphrase) throw new Error("passphrase is required to rotate");
      if (!isV2Envelope(details?.encryptedEncryptionKey)) {
        throw new Error("account must be unlocked and migrated to v2 first");
      }

      onProgress({ phase: "rotating-key", done: 0, total: 0 });

      // Unwrap the CURRENT key material (vault key + RSA private key).
      const current = await verifyPassphrase(
        passphrase,
        details.encryptedEncryptionKey,
        details.encryptedKey
      );
      if (!current.valid) throw new Error("wrong passphrase");

      const history = this.parseKeyHistory();
      const currentGeneration = history.length + 1;
      const newGeneration = currentGeneration + 1;

      // Mint a new vault key; re-wrap it and the unchanged RSA keypair.
      const newVaultKey = randomVaultKey();
      const wrapped = await rewrapToV2({
        passphrase,
        encryptionKey: newVaultKey,
        rsaPrivateKey: current.rsaPrivateKey,
        rsaPublicKey: details.publicKey,
      });

      // Carry the just-superseded wrapped key forward as history.
      const newHistory = [
        ...history,
        {
          generation: currentGeneration,
          encryptedEncryptionKey: details.encryptedEncryptionKey,
        },
      ];

      // Persist the rotation (server stores version + history verbatim).
      await authData.post(ENCRYPTION_ROTATE, {
        encryptedEncryptionKey: wrapped.encryptedEncryptionKey,
        encryptedKey: wrapped.encryptedPrivateKey,
        publicKey: wrapped.rsaPublicKey,
        encryptionKeyHistory: JSON.stringify(newHistory),
        encryptionKeyVersion: newGeneration,
        hint: details.hint,
      });
      await this.getEncryptionStatus();

      // Update the in-memory keyring: old generations (for reads) + the new
      // current generation (what new writes use).
      const entries = {};
      for (const h of newHistory) {
        const u = await unwrapV2(passphrase, h.encryptedEncryptionKey);
        if (u.valid) entries[h.generation] = u.encryptionKey;
      }
      entries[newGeneration] = newVaultKey;
      setKeyring(entries, newGeneration);

      // Make the new key the active session key.
      this.decryptedEncryptionKey = newVaultKey;
      setCookie("decryptedEncryptionKey", newVaultKey, { expires: 1 / 24 });

      // Re-encrypt every encrypted field from old generations to the new one.
      const stats = await this.reEncryptAllData(onProgress);

      // Re-wrap the new vault key for every accepted agent (handoff stays valid).
      onProgress({ phase: "re-wrapping-agents", done: 0, total: 0 });
      const agents = await this.rewrapVaultKeyForAllAgents();

      return { newGeneration, ...stats, agentsReWrapped: agents.wrapped, agentsFailed: agents.failed };
    },

    /**
     * Re-encrypt all encrypted customer-object fields to the current (newest)
     * key generation. One EncryptionService on the current key decrypts older
     * generations via the keyring and encrypts to the current one. Idempotent
     * and resumable: fields already at the current generation are skipped, so a
     * re-run finishes an interrupted rotation. Encrypted FILES are detected and
     * reported (filesPending) but not yet re-encrypted here — they stay readable
     * via the keyring until a follow-up file pass.
     */
    async reEncryptAllData(onProgress = () => {}) {
      const service = new EncryptionService(this.decryptedEncryptionKey);
      const currentGeneration = getCurrentGeneration() ?? 1;

      const resp = await authData.get(`${CUSTOMER_OBJECT}?includeFields=yes`);
      const objects = resp?.data?.data || [];

      // Collect the fields that need rotation (encrypted, older generation).
      const work = [];
      let filesPending = 0;
      for (const obj of objects) {
        for (const f of obj.fields || []) {
          const val = f.fieldValue;
          if (!val || typeof val !== "string") continue;
          const hasFile = f.fileId && String(f.fileId) !== "0";
          if (isV2DataEnvelope(val)) {
            let gen = 1;
            try {
              gen = dataEnvelopeGeneration(val);
            } catch {
              continue;
            }
            if (gen >= currentGeneration) continue; // already current
            if (hasFile) {
              filesPending++;
              continue; // file blobs are re-encrypted in a separate pass
            }
            work.push({ objectId: obj.id, fieldId: f.id, val });
          } else if (hasFile) {
            // legacy encrypted file — needs the file pass
            filesPending++;
          }
          // Non-v2, non-file values are treated as plaintext and left alone;
          // legacy AES-CBC field data (pre-step-1) is rare and, if present,
          // will be migrated the next time the field is edited in the UI.
        }
      }

      let fieldsRotated = 0;
      let failures = 0;
      const total = work.length;
      onProgress({ phase: "re-encrypting", done: 0, total });

      for (const item of work) {
        const plain = service.decrypt(item.val);
        // decrypt() returns a bracketed placeholder on failure — never PATCH it.
        if (
          typeof plain !== "string" ||
          (plain.startsWith("[") && plain.endsWith("]"))
        ) {
          failures++;
          onProgress({ phase: "re-encrypting", done: fieldsRotated, total });
          continue;
        }
        try {
          const newVal = service.encrypt(plain);
          await authData.patch(
            `${CUSTOMER_OBJECT}/${item.objectId}/fields/${item.fieldId}`,
            { fieldValue: newVal }
          );
          fieldsRotated++;
        } catch (error) {
          console.error("re-encrypt field failed:", error);
          failures++;
        }
        onProgress({ phase: "re-encrypting", done: fieldsRotated, total });
      }

      return { fieldsRotated, filesPending, failures };
    },

    /**
     * Agent handoff (step 3): wrap the CURRENT vault key with an accepted
     * agent's RSA public key and store it on the agent record, so that — only
     * after a verified passing — the agent can unwrap it with their own private
     * key and decrypt released data. The server never sees the vault key.
     * Requires the owner to be unlocked (vault key in memory); returns false
     * and skips otherwise (can be retried later). See docs §5, utils/encryptionV2.
     * @returns {Promise<boolean>} true if a wrapped key was stored
     */
    async wrapVaultKeyForAgent(agentId) {
      try {
        if (!this.decryptedEncryptionKey || this.isKeyExpired) {
          console.warn(
            "wrapVaultKeyForAgent: vault key unavailable; deferring for agent",
            agentId
          );
          return false;
        }
        const resp = await authData.get(AGENT_ENCRYPTION(agentId));
        const publicKey = resp?.data?.data?.publicKey;
        if (!publicKey) return false;
        const wrapped = wrapKeyForAgent(publicKey, this.decryptedEncryptionKey);
        await authData.post(AGENT_ENCRYPTION(agentId), {
          encryptedEncryptionKey: wrapped,
        });
        return true;
      } catch (error) {
        console.error("wrapVaultKeyForAgent failed:", error);
        return false;
      }
    },

    /**
     * Agent side of the handoff: obtain the owner's vault key that was wrapped
     * for THIS agent and unwrap it with the agent's own RSA private key.
     *
     * The logged-in user here is the agent. The server returns the wrapped key
     * only once the owner's account is in a released state (gated). The agent
     * unlocks their own RSA private key with their own passphrase, then unwraps.
     * The returned vault key feeds an EncryptionService to decrypt released
     * objects/stories/messages. See docs §5.
     *
     * @param {number} agentId  the agent-relationship id (from the agent's dashboard)
     * @param {string} agentPassphrase  the AGENT's own passphrase
     * @returns {Promise<string|null>} the owner's vault key, or null if unavailable
     */
    async unlockAgentReleaseKey(agentId, agentPassphrase) {
      try {
        // 1. Load the agent's OWN encryption material and unlock their RSA key.
        await this.getEncryptionStatus();
        const own = this.encryptionDetails;
        if (!own?.encryptedEncryptionKey || !own?.encryptedKey) return null;
        const unlocked = await verifyPassphrase(
          agentPassphrase,
          own.encryptedEncryptionKey,
          own.encryptedKey
        );
        if (!unlocked.valid || !unlocked.rsaPrivateKey) return null;

        // 2. Fetch the wrapped vault key (server gates this on release state).
        const resp = await authData.get(AGENT_RELEASE_KEY(agentId));
        const wrapped = resp?.data?.data?.encryptedEncryptionKey;
        if (!wrapped) return null;

        // 3. Unwrap it with the agent's RSA private key.
        return unwrapKeyAsAgent(unlocked.rsaPrivateKey, wrapped);
      } catch (error) {
        console.error("unlockAgentReleaseKey failed:", error);
        return null;
      }
    },

    /**
     * Re-wrap the current vault key for every accepted agent. Called after a
     * rotation so each agent's stored key matches the new vault key. Best-effort
     * and idempotent; reports counts for observability.
     * @returns {Promise<{wrapped:number, failed:number}>}
     */
    async rewrapVaultKeyForAllAgents() {
      try {
        const resp = await authData.get(AGENTS);
        const agents = resp?.data?.data?.myAgents || [];
        let wrapped = 0;
        let failed = 0;
        for (const a of agents) {
          if (a.status !== "accepted") continue;
          const ok = await this.wrapVaultKeyForAgent(a.id);
          ok ? wrapped++ : failed++;
        }
        return { wrapped, failed };
      } catch (error) {
        console.error("rewrapVaultKeyForAllAgents failed:", error);
        return { wrapped: 0, failed: 0 };
      }
    },

    async loseData(data) {
      this.loading = true;
      try {
        const result = await authData.delete(ENCRYPTION_DATA, data);
        return result;
      } catch (error) {
        console.error("Failed to delete encryption data:", error);
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },

    async getEncryptionStatistics(data) {
      this.loading = true;
      try {
        const result = await authData.get(ENCRYPTION_STATISTICS, data);
        this.encryptionStatistics = result.data.data;
        return result;
      } catch (error) {
        console.error("Failed to fetch encryption statistics:", error);
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },
  },
});
